import { ChevronRight } from "@mui/icons-material";
import { darken, List, ListItem, ListItemText } from "@mui/material";
import { useMemo } from "react";
import { generatePath, Link } from "react-router";

import { GqlWorkerVisitReportStatus } from "~/__generated__";
import UserBadge from "~/modules/user/components/UserBadge";

import { ProjectReviewVisitV2FieldworkersWithoutReportListFragment } from "./fragments.generated";

type Props = {
    visit: ProjectReviewVisitV2FieldworkersWithoutReportListFragment;
};

export function ProjectReviewVisitV2FieldworkersWithoutReportList({ visit }: Props) {
    const fieldworkersWithoutReports = useMemo(() => {
        const userIdsWithReports = visit.reportsV2.map(report => report.userId);
        const userIdsWithImpartialReports = visit.reportsV2
            .filter(report => report.status === GqlWorkerVisitReportStatus.AwaitingReport)
            .map(report => report.userId);
        return visit.fieldworkers.filter(
            fieldworker =>
                userIdsWithReports.includes(fieldworker.user.id) === false ||
                userIdsWithImpartialReports.includes(fieldworker.user.id),
        );
    }, [visit.reportsV2, visit.fieldworkers]);

    return (
        <List sx={theme => ({ padding: 0, background: theme.palette.background.paper })}>
            {fieldworkersWithoutReports.map(fieldworker => (
                <ListItem
                    key={fieldworker.id}
                    sx={theme => ({
                        borderTop: `1px solid ${theme.palette.divider}`,
                        columnGap: 1,
                        padding: 1,
                        color: theme.palette.text.primary,
                        "&:hover": {
                            backgroundColor: darken(theme.palette.background.paper, 0.03),
                        },
                    })}
                    dense
                    component={Link}
                    to={generatePath("/admin/users/:userId", { userId: fieldworker.user.id })}
                >
                    <UserBadge
                        size={24}
                        names={[fieldworker.user.firstName, fieldworker.user.lastName]}
                        profilePicture={fieldworker.user.profilePictureMedia?.url}
                    />
                    <ListItemText>
                        {fieldworker.user.firstName} {fieldworker.user.lastName}
                    </ListItemText>
                    <ChevronRight />
                </ListItem>
            ))}
        </List>
    );
}
